import { type ConnectionItemProps } from "@wayflyer/flyui";
import { IconPage24Line } from "@wayflyer/flyui-icons-next/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl, type MessageDescriptor } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS } from "../../../components/constants";
import type { AccountingDocumentsValidationResult } from "../../../hooks/useAccountingDocumentsValidation";
import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { UploadDocumentType, type BusinessModelData } from "../../../types";
import { isAccountingUploadRequired } from "../../../utils/funding-journey";

type ConnecitonItemKey = "skipped" | "uploaded" | "failedValidation" | "todo";

type RequiredDocumentType = UploadDocumentType.PROFIT_AND_LOSS | UploadDocumentType.BALANCE_SHEET;
const REQUIRED_DOCUMENT_TYPES = [UploadDocumentType.PROFIT_AND_LOSS, UploadDocumentType.BALANCE_SHEET] as const;

const DOC_TYPE_TO_NAME: Record<RequiredDocumentType, MessageDescriptor> = {
  [UploadDocumentType.PROFIT_AND_LOSS]: COMMON_MESSAGES.documentUploadProfitAndLoss,
  [UploadDocumentType.BALANCE_SHEET]: COMMON_MESSAGES.documentUploadBalanceSheet,
};

export const useValidatedDocumentsAsConnectionItems = ({
  fundingApplication,
  countryOfIncorporation,
  businessModel,
  validatedDocuments,
  validationResults,
  onDocumentRemove,
}: {
  fundingApplication: FormattedFundingApplicationScreen;
  businessModel: BusinessModelData;
  countryOfIncorporation: string;
  validatedDocuments: FundingApplicationSchemas.ListDocumentsResponse[] | undefined;
  validationResults: Record<RequiredDocumentType, AccountingDocumentsValidationResult>;
  onDocumentRemove: (type: UploadDocumentType, required: boolean) => void;
}): ConnectionItemProps[] => {
  const intl = useIntl();
  const { navigate } = useSpaNavigate("FundingApp");
  const areAccountingDocumentsRequired = isAccountingUploadRequired(
    businessModel,
    fundingApplication,
    countryOfIncorporation
  );
  const isMarkedAsSkipped = fundingApplication.accountingOptedOut;

  if (!areAccountingDocumentsRequired) {
    return [];
  }

  const getConnectionItemByStatus: Record<
    ConnecitonItemKey,
    (
      docType: RequiredDocumentType,
      matchingDocs?: FundingApplicationSchemas.ListDocumentsResponse
    ) => ConnectionItemProps
  > = {
    skipped: (docType) =>
      ({
        testId: `document-skipped-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage({ id: "funding-application:completed", defaultMessage: "Completed" }),
      } satisfies ConnectionItemProps),

    uploaded: (docType, matchingDocs) =>
      ({
        testId: `document-uploaded-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.verified),
        subtitle: (
          <ul>
            {matchingDocs?.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      } satisfies ConnectionItemProps),

    failedValidation: (docType, matchingDocs) =>
      ({
        testId: `document-failed-validation-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        badgeStatus: "warning",
        badgeText: intl.formatMessage(COMMON_MESSAGES.needsVerification),
        actions: [
          {
            content: intl.formatMessage(COMMON_MESSAGES.verify),
            onAction: () => navigate(`${CONNECT_APP_ROUTE_PATHS.VERIFY}?document=${docType}`),
          },
        ],
        subtitle: (
          <ul>
            {matchingDocs?.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      } satisfies ConnectionItemProps),

    todo: (docType) =>
      ({
        testId: `document-todo-${docType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[docType]),
        logo: <IconPage24Line />,
        actions: [
          {
            critical: true,
            content: intl.formatMessage({
              id: "funding-application: remove platform label",
              defaultMessage: "Remove",
            }),
            onAction: () => onDocumentRemove(docType, true),
          },
          {
            content: intl.formatMessage(COMMON_MESSAGES.upload),
            onAction: () => navigate(`${CONNECT_APP_ROUTE_PATHS.UPLOADS}?document=${docType}`),
          },
        ],
      } satisfies ConnectionItemProps),
  };

  const items: ConnectionItemProps[] = REQUIRED_DOCUMENT_TYPES.map((documentType) => {
    const matchingDocuments = (validatedDocuments ?? []).find((doc) => doc.documentType === documentType);
    let status: ConnecitonItemKey;

    if (isMarkedAsSkipped) {
      status = "skipped";
    } else if (matchingDocuments) {
      if (validationResults[documentType].status === "failed-validation") {
        status = "failedValidation";
      } else {
        status = "uploaded";
      }
    } else {
      status = "todo";
    }

    return getConnectionItemByStatus[status](documentType, matchingDocuments);
  });

  return items;
};
